import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { Guid } from 'tms-lib';

import { TmsWebBaseEditService } from '../base';
import {
  ApiResult,
  ManufacturedReceiptLabels,
  PickingListLabel,
  ProductLabelPiecesRequest,
  ProductStockAllocations,
  PurchaseOrderLineStockAllocation,
  PurchaseOrderLineStockIssue,
  SalesOrderLineAllocationsSummaryTop,
  SalesOrderStockAllocation,
  SalesOrPurchaseOrderStockAllocation,
  StockItem,
  StockItemAllocation,
  StockItemAllocations,
  StockItemCosts,
  StockItemDetails,
  StockItemLabel,
  StockItemOrderHistory,
  StockItemSearchResults,
  StockItemSplit,
  StockItemTransactionAnalysis,
  SupplementaryInvoiceCost,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class StockItemService extends TmsWebBaseEditService<StockItemSearchResults, StockItemDetails> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('StockItem', $localize`Stock Item`);
  }

  // Allocated stock moved into a location marked Do Not Allocate keeps its allocation; the save
  // is never blocked, so the warning is asked for afterwards and shown here - subscribed in the
  // service so it still lands if the form has already closed.
  notifyLocationCautions(stockItemId: string) {
    if (!stockItemId || stockItemId === Guid.empty) {
      return;
    }

    this.httpService
      .get<ApiResult>(`${this.apiUrl}/LocationCautions?stockItemId=${stockItemId}`)
      .subscribe((result: ApiResult) => {
        if (result?.cautions?.length) {
          this.notificationService.warning(result.cautions.join('\n'));
        }
      });
  }

  salesOrderStockAllocation(id: string): Observable<SalesOrderStockAllocation[]> {
    return this.httpService.get<SalesOrderStockAllocation[]>(`${this.apiUrl}/SalesOrderStockAllocation?id=${id}`);
  }

  stockAllocationsSummaryTop(stockItemId: string): Observable<SalesOrderLineAllocationsSummaryTop> {
    return this.httpService.get<SalesOrderLineAllocationsSummaryTop>(
      `${this.apiUrl}/StockAllocationsSummaryTop?stockItemId=${stockItemId}`,
    );
  }

  salesOrPurchaseOrderStockAllocation(id: string): Observable<SalesOrPurchaseOrderStockAllocation[]> {
    return this.httpService.get<SalesOrPurchaseOrderStockAllocation[]>(
      `${this.apiUrl}/SalesOrPurchaseOrderStockAllocation?id=${id}`,
    );
  }

  stockItemAllocations(id: string): Observable<StockItemAllocations> {
    return this.httpService.get<StockItemAllocations>(`${this.apiUrl}/StockItemAllocation?id=${id}`);
  }

  productStockAllocations(id: string): Observable<ProductStockAllocations> {
    return this.httpService.get<ProductStockAllocations>(`${this.apiUrl}/ProductStockAllocation?id=${id}`);
  }

  stockItemsByProduct(id: string): Observable<StockItem[]> {
    return this.httpService.get<StockItem[]>(`${this.apiUrl}/StockItemsByProduct?id=${id}`);
  }

  allocationsForStockItem(id: string): Observable<StockItemAllocation[]> {
    return this.httpService.get<StockItemAllocation[]>(`${this.apiUrl}/AllocationsForStockItem?id=${id}`);
  }

  // The received pieces that get a Product Label printed automatically - the API applies the
  // Product Type setting and the document's auto-print, so every receipt screen asks the same way.
  productLabelPieces(request: ProductLabelPiecesRequest): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/ProductLabelPieces`, request);
  }

  splitsForStockItem(id: string): Observable<StockItemSplit[]> {
    return this.httpService.get<StockItemSplit[]>(`${this.apiUrl}/SplitsForStockItem?id=${id}`);
  }

  stockItemCosts(id: string): Observable<StockItemCosts> {
    return this.httpService.get<StockItemCosts>(`${this.apiUrl}/StockItemCosts?id=${id}`);
  }

  stockItemSupplementaryCosts(id: string): Observable<SupplementaryInvoiceCost[]> {
    return this.httpService.get<SupplementaryInvoiceCost[]>(`${this.apiUrl}/StockItemSupplementaryCosts?id=${id}`);
  }

  stockItemOrderHistory(id: string): Observable<StockItemOrderHistory[]> {
    return this.httpService.get<StockItemOrderHistory[]>(`${this.apiUrl}/StockItemOrderHistory?id=${id}`);
  }

  callOffStock(id: string): Observable<StockItemDetails[]> {
    return this.httpService.get<StockItemDetails[]>(`${this.apiUrl}/CallOffStock?id=${id}`);
  }

  createForProduct(id: string): Observable<StockItemDetails> {
    return this.httpService.get<StockItemDetails>(`${this.apiUrl}/CreateForProduct?id=${id}`);
  }

  stockItemLabel(id: string): Observable<StockItemLabel> {
    return this.httpService.get<StockItemLabel>(`${this.apiUrl}/StockItemLabel?id=${id}`);
  }

  getStockItemLabel(id: string, prevLocation: boolean): Observable<PickingListLabel> {
    return this.httpService.get<PickingListLabel>(
      `${this.apiUrl}/GetStockItemLabel?id=${id}&previousLocation=${prevLocation}`,
    );
  }

  getSplitStockItemLabel(id: string): Observable<PickingListLabel> {
    return this.httpService.get<PickingListLabel>(`${this.apiUrl}/GetSplitStockItemLabel?id=${id}`);
  }

  getStockItemLabelByStockNo(stockNo: string): Observable<PickingListLabel> {
    return this.httpService.get<PickingListLabel>(`${this.apiUrl}/GetStockItemLabelByStockNo?stockNo=${stockNo}`);
  }

  // Extra labels (customer stock + delivery address) for a manufactured stock item received against a
  // PO. Both are null when the receipt is not manufactured or not linked to a sales order.
  getManufacturedReceiptLabels(stockNo: string): Observable<ManufacturedReceiptLabels> {
    return this.httpService.get<ManufacturedReceiptLabels>(
      `${this.apiUrl}/GetManufacturedReceiptLabels?stockNo=${stockNo}`,
    );
  }

  getForLabels(ids: string[]): Observable<PickingListLabel[]> {
    return this.httpService.post<PickingListLabel[]>(`${this.apiUrl}/GetForLabels`, { ids });
  }

  getForLabelsByHeaderIds(ids: string[]): Observable<PickingListLabel[]> {
    return this.httpService.post<PickingListLabel[]>(`${this.apiUrl}/GetForLabelsByHeaderIds`, { ids });
  }

  exportStockToFTP(): Observable<any> {
    return this.httpService.get<any>(`${this.apiUrl}/FtpCsvStockFile`);
  }

  ftpStockData(): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/FtpStockData`, {});
  }

  exportMagentoFtpStockUpdate(webjob: boolean): Observable<any> {
    return this.httpService.get<any>(`${this.apiUrl}/MagentoFtpStockUpdate?webjob=${webjob}`);
  }

  checkStockNo(stockNo: string, id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckStockNo?stockNo=${stockNo}&id=${id ?? ''}`);
  }

  purchaseOrderStockAllocation(
    id: string,
    purchaseOrderLineRequirementId: string,
    initialDisplay: boolean,
  ): Observable<PurchaseOrderLineStockAllocation[]> {
    return this.httpService.get<PurchaseOrderLineStockAllocation[]>(
      `${this.apiUrl}/PurchaseOrderLineStockAllocation?id=${id}&purchaseOrderLineRequirementId=${purchaseOrderLineRequirementId}&initialDisplay=${initialDisplay}`,
    );
  }

  purchaseOrderStockIssues(id: string): Observable<PurchaseOrderLineStockIssue[]> {
    return this.httpService.get<PurchaseOrderLineStockIssue[]>(`${this.apiUrl}/PurchaseOrderLineStockIssue?id=${id}`);
  }

  getStockItemTransactionAnalysis(
    search: string,
    fromDate: Date,
    toDate: Date,
  ): Observable<StockItemTransactionAnalysis[]> {
    return this.httpService.get<StockItemTransactionAnalysis[]>(
      `${this.apiUrl}/StockItemTransactionAnalysis?search=${search}&fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`,
    );
  }
}
